import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';

import type { JobThreeDFiles } from 'src/shared/graphql/generated';
import { useMode } from 'src/shared/hooks';
import { EventType, emitPageAction } from 'src/shared/telemetry';
import { requestWithRetry } from 'src/shared/utils/request';

import { fetchUrls } from './fetchUrls';
import type {
  Data,
  Geometry,
  IFetchDataWrapperParams,
  IUseFetchModeDataParams,
  Metadata,
} from './types';

const fetchData = async (urls: JobThreeDFiles) => {
  try {
    invariant(
      urls.metadataUrl,
      'Missing metadataUrl  - Model can not be fully loaded',
    );

    invariant(
      urls.geometryUrl,
      'Missing geometryUrl - Model can not be fully loaded',
    );

    if (urls.geometryUrl && urls.metadataUrl) {
      const [geometry, metadata] = await Promise.all([
        requestWithRetry(urls.geometryUrl).then(res => res.json() as Geometry),
        requestWithRetry(urls.metadataUrl).then(res => res.json() as Metadata),
      ]);

      return { geometry, metadata };
    }
  } catch (error) {
    captureException(error);
  }

  return null;
};

// Refactor to remove setGeometry and setMetadata
const fetchDataWrapper = async ({
  jobId,
  geometryUrl,
  metadataUrl,
  setGeometry,
  setMetadata,
  mode,
}: IFetchDataWrapperParams) => {
  let data: Data = null;

  if (geometryUrl && metadataUrl) {
    // if we already have the urls, we can fetch the data
    data = await fetchData({ geometryUrl, metadataUrl });
  } else if (jobId) {
    // if we have a jobId, we can fetch the data
    const urls = await fetchUrls(jobId);
    data = await fetchData(urls);
  }

  if (data) {
    setGeometry(data.geometry);
    setMetadata(data.metadata);

    emitPageAction(EventType.GEOMETRY_AND_METADATA_LOADED, {
      designStudioMode: mode,
    });
  }
  return { geometry: data?.geometry, metadata: data?.metadata };
};

const useFetchModelData = ({
  jobId,
  geometryUrl,
  metadataUrl,
}: IUseFetchModeDataParams) => {
  const [geometry, setGeometry] = useState<Geometry>(null);
  const [metadata, setMetadata] = useState<Metadata>(null);
  const mode = useMode();

  useEffect(() => {
    void fetchDataWrapper({
      jobId,
      geometryUrl,
      metadataUrl,
      setGeometry,
      setMetadata,
      mode,
    });
  }, [geometryUrl, jobId, metadataUrl, mode]);

  return { geometry, metadata };
};

export { useFetchModelData, fetchData, fetchDataWrapper };

export * from './types';
